import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Users, Shield, X, AlertCircle, Check, Swords, Shuffle, UserPlus, Lock } from 'lucide-react';
import './MPTeamSelectionModal.css';

const MPTeamSelectionModal = ({ tournament, user, onSelectTeam, onClose, loading, t }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamPreference, setTeamPreference] = useState('random');
  const [teammates, setTeammates] = useState(['', '', '']); // Up to 3 teammates for 4v4

  // Calculate teams (Team 1 vs Team 2) with availability info
  const teams = useMemo(() => {
    const maxTeamSize = Math.ceil(tournament.maxPlayers / 2);
    const teamsArray = [];
    const playerName = (user.inGameName || user.username || '').toLowerCase().trim();

    for (let teamNumber = 1; teamNumber <= 2; teamNumber++) {
      const teamMembers = (tournament.mpTeamAssignments || [])
        .filter(assignment => assignment.teamNumber === teamNumber)
        .map(assignment => ({
          userId: (assignment.userId?._id || assignment.userId)?.toString(),
          username: assignment.username,
          inGameName: assignment.inGameName
        }));

      // Get team settings if they exist
      const teamKey = teamNumber === 1 ? 'team1' : 'team2';
      const teamSettings = tournament.mpTeamSettings?.[teamKey] || null;

      const isFull = teamMembers.length >= maxTeamSize;
      const isEmpty = teamMembers.length === 0;

      // Check if team has squad restrictions that block this user
      let hasSquadRestriction = false;
      let canUserJoin = true;
      let joinReason = '';

      if (!isFull && !isEmpty && teamSettings?.preference === 'specific') {
        const teammates = teamSettings.teammates || [];

        // Check if player is in the specific list
        const isInvited = teammates.some(tm =>
          !tm.isRandom && tm.name && tm.name.toLowerCase().trim() === playerName
        );

        // Check if there are random slots
        const hasRandomSlots = teammates.some(tm => tm.isRandom);

        // Check if there's no valid teammate entry (treating as closed)
        const hasValidEntries = teammates.some(tm => tm.name || tm.isRandom);

        if (!hasValidEntries) {
          hasSquadRestriction = true;
          canUserJoin = false;
          joinReason = 'squadOnly';
        } else if (!isInvited && !hasRandomSlots) {
          hasSquadRestriction = true;
          canUserJoin = false;
          joinReason = 'squadOnly';
        }
      }

      teamsArray.push({
        teamNumber,
        members: teamMembers,
        isFull,
        spotsRemaining: maxTeamSize - teamMembers.length,
        maxSize: maxTeamSize,
        isEmpty,
        settings: teamSettings,
        hasSquadRestriction,
        canUserJoin: isFull ? false : (isEmpty ? true : canUserJoin),
        joinReason,
        isAvailable: !isFull && !hasSquadRestriction
      });
    }

    return teamsArray;
  }, [tournament, user]);

  // Check if user can join the selected team based on settings
  const canJoinSelectedTeam = useMemo(() => {
    if (selectedTeam === null) return { canJoin: false, reason: '' };

    const team = teams.find(t => t.teamNumber === selectedTeam);
    if (!team) return { canJoin: false, reason: 'Team not found' };
    if (team.isFull) return { canJoin: false, reason: t ? t('teamFull', 'Team is full') : 'Team is full' };
    if (team.isEmpty) return { canJoin: true, isCaptain: true, reason: t ? t('youWillBeCaptain', 'You will be the team captain') : 'You will be the team captain' };

    // Check if team has settings with restrictions
    if (team.settings?.preference === 'specific') {
      const playerName = (user.inGameName || user.username || '').toLowerCase().trim();
      const teammatesData = team.settings.teammates || [];

      // Check if player is in the specific list
      const isInvited = teammatesData.some(tm =>
        !tm.isRandom && tm.name && tm.name.toLowerCase().trim() === playerName
      );

      // Check if there are random slots
      const hasRandomSlots = teammatesData.some(tm => tm.isRandom);

      if (isInvited) {
        return { canJoin: true, reason: t ? t('youAreInvited', 'You are invited to this team') : 'You are invited to this team' };
      }

      if (hasRandomSlots) {
        return { canJoin: true, reason: t ? t('joiningViaRandomSlot', 'Joining via random slot') : 'Joining via random slot' };
      }

      // Check if there's no valid teammate entry (treating as closed)
      if (teammatesData.length === 0 || !teammatesData.some(tm => tm.name || tm.isRandom)) {
        return { canJoin: false, reason: t ? t('teamNotAccepting', 'This team is not accepting players') : 'This team is not accepting players' };
      }

      return {
        canJoin: false,
        reason: t ? t('squadOnly', 'Squad Only') : 'Squad Only'
      };
    }

    return { canJoin: true, reason: '' };
  }, [selectedTeam, teams, user, t]);

  // Is the selected team empty (making user the captain)?
  const isBecomingCaptain = useMemo(() => {
    if (selectedTeam === null) return false;
    const team = teams.find(t => t.teamNumber === selectedTeam);
    return team?.isEmpty || false;
  }, [selectedTeam, teams]);

  // Max teammates for this tournament (team size - 1 for captain)
  const maxTeammates = useMemo(() => {
    const maxTeamSize = Math.ceil(tournament.maxPlayers / 2);
    return maxTeamSize - 1;
  }, [tournament]);

  // Handle teammate input change
  const handleTeammateChange = (index, value) => {
    const newTeammates = [...teammates];
    newTeammates[index] = value;
    setTeammates(newTeammates);
  };

  // Toggle teammate slot between specific/random/empty
  const toggleTeammateSlot = (index) => {
    const newTeammates = [...teammates];
    const currentValue = newTeammates[index];

    if (currentValue === '') {
      newTeammates[index] = '__RANDOM__';
    } else if (currentValue === '__RANDOM__') {
      newTeammates[index] = '';
    } else {
      newTeammates[index] = '__RANDOM__';
    }

    setTeammates(newTeammates);
  };

  // Handle team card click
  const handleTeamClick = (team) => {
    // Only allow selection if team is available (not full and no squad restriction)
    if (team.isAvailable) {
      setSelectedTeam(team.teamNumber);
    }
  };

  const handleJoinTeam = () => {
    if (selectedTeam !== null && canJoinSelectedTeam.canJoin) {
      // Format teammates as objects for the API
      const formattedTeammates = teammates
        .slice(0, maxTeammates)
        .filter(name => name !== '')
        .map(name => ({
          name: name === '__RANDOM__' ? '' : name,
          isRandom: name === '__RANDOM__'
        }));

      onSelectTeam(selectedTeam, isBecomingCaptain ? teamPreference : null, formattedTeammates);
    }
  };

  // Use portal to render modal at document body level to avoid z-index stacking context issues
  return ReactDOM.createPortal(
    <div className="mp-team-modal-overlay">
      <div className="mp-team-modal">
        {/* Header */}
        <div className="mp-team-modal-header">
          <div className="mp-team-modal-icon">
            <Swords className="w-6 h-6 text-white" />
          </div>
          <h3 className="mp-team-modal-title">
            {t ? t('selectTeam', 'Select Your Team') : 'Select Your Team'}
          </h3>
          <button onClick={onClose} className="mp-team-modal-close" disabled={loading}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info */}
        <div className="mp-team-modal-info">
          <AlertCircle className="w-5 h-5 text-orange-400" />
          <p>
            {t ? t('mpTeamSelectInfo', 'Choose which team to join for this match') : 'Choose which team to join for this match'}
          </p>
        </div>

        {/* Teams Grid - Side by Side */}
        <div className="mp-team-modal-content">
          <div className="mp-teams-vs-container">
            {teams.map((team, index) => (
              <React.Fragment key={team.teamNumber}>
                <div
                  className={`mp-team-card ${
                    selectedTeam === team.teamNumber ? 'selected' : ''
                  } ${!team.isAvailable ? 'disabled' : ''} team-${team.teamNumber}`}
                  onClick={() => handleTeamClick(team)}
                >
                  <div className="mp-team-card-header">
                    <div className={`mp-team-number team-${team.teamNumber}`}>
                      <Shield className="w-5 h-5" />
                      {t ? t('team', 'Team') : 'Team'} {team.teamNumber}
                    </div>
                    {selectedTeam === team.teamNumber && team.isAvailable && (
                      <div className="mp-team-selected-badge">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                    {team.hasSquadRestriction && (
                      <div className="mp-team-squad-badge">
                        <Lock className="w-3 h-3" />
                      </div>
                    )}
                  </div>

                  <div className="mp-team-roster">
                    <div className="mp-team-roster-label">
                      {team.members.length}/{team.maxSize} {t ? t('players', 'Players') : 'Players'}
                    </div>
                    <div className="mp-team-members">
                      {team.members.map((member, idx) => (
                        <div key={idx} className="mp-team-member">
                          <Users className="w-3 h-3" />
                          <span>{member.inGameName || member.username}</span>
                        </div>
                      ))}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <div key={`empty-${idx}`} className="mp-team-member empty">
                          <Users className="w-3 h-3" />
                          <span>{t ? t('emptySlot', 'Empty Slot') : 'Empty Slot'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={`mp-team-status ${
                    team.isFull ? 'full' :
                    team.hasSquadRestriction ? 'squad-only' :
                    'available'
                  }`}>
                    {team.isFull ? (
                      <>
                        <span>❌ {t ? t('teamFull', 'Team Full') : 'Team Full'}</span>
                      </>
                    ) : team.hasSquadRestriction ? (
                      <>
                        <Lock className="w-3 h-3" />
                        <span>{t ? t('squadOnly', 'Squad Only') : 'Squad Only'}</span>
                      </>
                    ) : (
                      <>
                        <span>✅ {team.spotsRemaining} {t ? t('spotsAvailable', 'spots available') : 'spots available'}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* VS Divider between teams */}
                {index === 0 && (
                  <div className="mp-teams-vs-divider">
                    <span className="vs-text">VS</span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Captain Settings - shown when joining an empty team */}
        {isBecomingCaptain && selectedTeam !== null && (
          <div className="mp-team-captain-settings">
            <div className="mp-team-captain-header">
              <Shield className="w-5 h-5 text-yellow-400" />
              <span>{t ? t('captainSettings', 'Team Captain Settings') : 'Team Captain Settings'}</span>
            </div>
            <p className="mp-team-captain-info">
              {t ? t('captainSettingsInfo', 'As team captain, choose who can join your team') : 'As team captain, choose who can join your team'}
            </p>

            {/* Team Preference Selection */}
            <div className="mp-team-preference-section">
              <label className="mp-team-preference-label">
                {t ? t('teamJoinMode', 'Team Join Mode') : 'Team Join Mode'}
              </label>
              <select
                value={teamPreference}
                onChange={(e) => {
                  setTeamPreference(e.target.value);
                  if (e.target.value === 'random') {
                    setTeammates(['', '', '']);
                  }
                }}
                className="mp-team-preference-select"
              >
                <option value="random">{t ? t('randomTeammates', 'Random - Anyone can join') : 'Random - Anyone can join'}</option>
                <option value="specific">{t ? t('specificTeammates', 'Specific - Choose teammates') : 'Specific - Choose teammates'}</option>
              </select>
            </div>

            {/* Specific Teammates Input */}
            {teamPreference === 'specific' && maxTeammates > 0 && (
              <div className="mp-team-teammates-section">
                <label className="mp-team-teammates-label">
                  <UserPlus className="w-4 h-4" />
                  {t ? t('selectTeammates', 'Select Teammates (by In-Game Name)') : 'Select Teammates (by In-Game Name)'}
                </label>
                <div className="mp-team-teammates-grid">
                  {Array.from({ length: maxTeammates }).map((_, index) => {
                    const value = teammates[index] || '';
                    const isRandom = value === '__RANDOM__';
                    const isSpecific = value !== '' && !isRandom;

                    return (
                      <div key={index} className="mp-team-teammate-slot">
                        <div className="mp-team-teammate-header">
                          <span className="mp-team-teammate-number">
                            {t ? t('slot', 'Slot') : 'Slot'} {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleTeammateSlot(index)}
                            className={`mp-team-toggle-btn ${isRandom ? 'random' : value ? 'specific' : 'empty'}`}
                            title={isRandom ? (t ? t('clickToEmpty', 'Click to leave empty') : 'Click to leave empty') :
                                   isSpecific ? (t ? t('clickForRandom', 'Click for random') : 'Click for random') :
                                   (t ? t('clickForRandom', 'Click for random') : 'Click for random')}
                          >
                            {isRandom ? (
                              <>
                                <Shuffle className="w-3 h-3" />
                                {t ? t('random', 'Random') : 'Random'}
                              </>
                            ) : (
                              <>
                                <Shuffle className="w-3 h-3" />
                                {t ? t('setRandom', 'Set Random') : 'Set Random'}
                              </>
                            )}
                          </button>
                        </div>
                        {!isRandom && (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handleTeammateChange(index, e.target.value)}
                            placeholder={t ? t('enterInGameName', 'Enter in-game name') : 'Enter in-game name'}
                            className="mp-team-teammate-input"
                            maxLength={30}
                          />
                        )}
                        {isRandom && (
                          <div className="mp-team-teammate-random-placeholder">
                            <Shuffle className="w-4 h-4" />
                            <span>{t ? t('anyPlayerCanJoin', 'Any player can join this slot') : 'Any player can join this slot'}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="mp-team-teammates-note">
                  {t ? t('teammatesNote', 'Leave empty slots to prevent others from joining your team') : 'Leave empty slots to prevent others from joining your team'}
                </p>
              </div>
            )}

            {teamPreference === 'random' && (
              <div className="mp-team-random-info">
                <Shuffle className="w-4 h-4" />
                <span>{t ? t('randomTeamInfo', 'Any player will be able to join your team') : 'Any player will be able to join your team'}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mp-team-modal-actions">
          <button
            onClick={handleJoinTeam}
            disabled={loading || selectedTeam === null || !canJoinSelectedTeam.canJoin}
            className="mp-team-btn mp-team-btn-join"
          >
            {loading
              ? (t ? t('joining', 'Joining...') : 'Joining...')
              : isBecomingCaptain
                ? (t ? t('createTeam', 'Create Team') : 'Create Team') + (selectedTeam ? ` ${selectedTeam}` : '')
                : (t ? t('joinTeam', 'Join Team') : 'Join Team') + (selectedTeam ? ` ${selectedTeam}` : '')
            }
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="mp-team-btn mp-team-btn-cancel"
          >
            {t ? t('cancel', 'Cancel') : 'Cancel'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MPTeamSelectionModal;
